import type { FunctionComponent } from "react";
import { t } from "i18next";
import styled, { keyframes } from "styled-components";
import { SubConditionItem } from "../SubConditionItem/SubConditionItem";
import { CurrentItemCodeBlock } from "./CurrentItemCodeBlock";
import type { CurrentItemView } from "./get-current-item";

interface CurrentItemBoxProps {
  item: CurrentItemView;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// Separates the Current Item box from the iteration's first action below it
// (matches the design's spacing between the box and "Update Record"). The
// margin lives here, not on SubConditionItem, which is also used tightly inside
// branch condition cards. The fade-in softens the skeleton -> content swap
// after a lazy loop-item fetch resolves.
const StyledWrapper = styled.div`
  margin-bottom: 16px;
  animation: ${fadeIn} 150ms ease-out;

  /* A key-value array item renders one pair per line (get-current-item's
     keyValueItemLines) -- inherited by SubConditionItem's value text, which
     otherwise collapses the newlines. */
  white-space: pre-line;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Figma: caption "Current Item" on top, "Record Title: {title}" (or the
// field-value / array-item equivalent) as the value line -- SubConditionItem
// already renders exactly caption-over-value. A structured (webhook object)
// item carries `pairs` and renders the code-block view instead.
export const CurrentItemBox: FunctionComponent<CurrentItemBoxProps> = ({
  item,
}) => (
  <StyledWrapper>
    {item.pairs !== undefined && item.pairs.length > 0 ? (
      <CurrentItemCodeBlock pairs={item.pairs} />
    ) : (
      <SubConditionItem
        caption={t("job-list.current-item")}
        value={`${t(item.caption)}: ${item.value}`}
      />
    )}
  </StyledWrapper>
);
